import { useState } from "react";

import { Button, ConfirmDialog, Typography, useTheme } from "@smartrent/ui";
import { useModalState } from "@smartrent/hooks";

import { DataDogLink } from "@/common/links/DataDogLink";
import { hubDebugLogs } from "@/utils/datadog";
import { Filter } from "@/lib/ansi-to-html";
import { useHubCommandMutation, useHubCommandsQuery } from "@/api";
import { DeviceProps, HubDetails, HubRequest, PermissionProps } from "@/types";

const REMOVE_FAILED_NODES_SCRIPT_NAME = "Remove Failed Nodes";
const PISTON_DEVICES_INFO_SCRIPT_NAME = "Piston Devices Info";
const REPORT_FAILED_NODE_IDS_SCRIPT_NAME = "Report Failed Node IDs";
const ansiConverter = new Filter({ newline: true });

export default function HubHeader({
  hub,
  permissions,
  devices,
}: {
  hub?: Partial<HubRequest & HubDetails>;
  permissions: PermissionProps;
  devices: DeviceProps[];
}) {
  const { colors } = useTheme();
  const [scriptOutputs, setScriptOutputs] = useState<Record<string, string>>(
    {}
  );
  const [loadingScriptId, setLoadingScriptId] = useState<string | null>(null);
  const { visible, onOpen, onClose } = useModalState();
  const { data: commandsResponse } = useHubCommandsQuery(
    { serial: hub?.serial as string },
    { enabled: !!(hub?.serial && permissions.advanced_hub_troubleshooting) }
  );
  const [hubCommand, { isLoading: isSendingCommand }] = useHubCommandMutation();

  if (!(hub && permissions.view_technical_details)) {
    return null;
  }

  const removeFailedNodesScript = commandsResponse?.scripts?.find(
    (script) => script.name === REMOVE_FAILED_NODES_SCRIPT_NAME
  );
  const pistonDevicesInfoScript = commandsResponse?.scripts?.find(
    (script) => script.name === PISTON_DEVICES_INFO_SCRIPT_NAME
  );
  const reportFailedNodeIdsScript = commandsResponse?.scripts?.find(
    (script) => script.name === REPORT_FAILED_NODE_IDS_SCRIPT_NAME
  );

  const sendScript = async (scriptId: string) => {
    setLoadingScriptId(scriptId);
    try {
      const output = await hubCommand({
        serial: hub.serial as string,
        command: scriptId,
      });
      setScriptOutputs((prev) => ({ ...prev, [scriptId]: output }));
    } finally {
      setLoadingScriptId(null);
    }
  };

  const renderScriptOutput = (scriptId?: string) => {
    const output = scriptId ? scriptOutputs[scriptId] : undefined;
    if (!output) {
      return null;
    }
    return (
      <div
        style={{
          padding: 16,
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          fontFamily: "monospace",
          backgroundColor: colors.black,
          color: colors.white,
          borderRadius: 4,
          lineHeight: 1.25,
        }}
        dangerouslySetInnerHTML={{
          __html: ansiConverter.toHtml(output),
        }}
      />
    );
  };

  return (
    <>
      <div>
        {permissions.advanced_hub_troubleshooting &&
        hub.type === "smartrent" &&
        devices?.length ? (
          <>
            <Typography type="bodyLargeSemibold">iEX Scripts</Typography>

            {removeFailedNodesScript ? (
              <>
                <Button
                  variation="plain"
                  isLoading={loadingScriptId === removeFailedNodesScript.id}
                  disabled={
                    isSendingCommand &&
                    loadingScriptId !== removeFailedNodesScript.id
                  }
                  onPress={onOpen}
                >
                  Remove Failed Nodes (Support Script)
                </Button>
                {renderScriptOutput(removeFailedNodesScript.id)}
              </>
            ) : null}

            {pistonDevicesInfoScript ? (
              <>
                <Button
                  variation="plain"
                  isLoading={loadingScriptId === pistonDevicesInfoScript.id}
                  disabled={
                    isSendingCommand &&
                    loadingScriptId !== pistonDevicesInfoScript.id
                  }
                  onPress={() => sendScript(pistonDevicesInfoScript.id)}
                >
                  Piston Devices Info (Support Script)
                </Button>
                {renderScriptOutput(pistonDevicesInfoScript.id)}
              </>
            ) : null}

            {reportFailedNodeIdsScript ? (
              <>
                <Button
                  variation="plain"
                  isLoading={loadingScriptId === reportFailedNodeIdsScript.id}
                  disabled={
                    isSendingCommand &&
                    loadingScriptId !== reportFailedNodeIdsScript.id
                  }
                  onPress={() => sendScript(reportFailedNodeIdsScript.id)}
                >
                  Report Failed Node IDs (Support Script)
                </Button>
                {renderScriptOutput(reportFailedNodeIdsScript.id)}
              </>
            ) : null}
          </>
        ) : null}
      </div>
      <div className="text-warning u-font10 u-flex u-flex-justify-between u-pleft4 u-pright4">
        {hub.id ? (
          <div>
            id: {hub.id}, hub.remote_id: {hub.remote_id}
          </div>
        ) : null}

        <div>
          <DataDogLink
            href={hubDebugLogs(hub)}
            target="_blank"
            rel="noopener noreferrer"
            iconProps={{ className: "dark:fill-foreground" }}
          />
        </div>
      </div>

      <ConfirmDialog
        title="Are you sure?"
        description="This will send the 'Remove Failed Nodes' Support Script to the hub, unconditionally removing all currently-failed Z-Wave nodes. This is irreversible and does not preserve any manual keep-list. If the unit's primary lock is currently a failed node, running this script will lock everyone out of the unit and require a drill out."
        visible={visible}
        onClose={onClose}
        onConfirm={async () => {
          onClose();
          if (removeFailedNodesScript) {
            await sendScript(removeFailedNodesScript.id);
          }
        }}
      />
    </>
  );
}
